// frontend/src/pages/admin/AdminPackagePowerRatingsPage.jsx
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Save, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageSEO } from '../../components/SEO/PageSEO';
import config from '../../config';
import api from '../../services/api';

export const AdminPackagePowerRatingsPage = () => {
  const [powerRatings, setPowerRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    fetchPowerRatings();
  }, []);

  const fetchPowerRatings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/products/power-ratings/');
      setPowerRatings(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error fetching power ratings:', error);
      toast.error('خطا در دریافت توان‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.value || !formData.label) {
      toast.error('لطفاً مقدار و عنوان را وارد کنید');
      return;
    }

    try {
      const accessToken = localStorage.getItem('access_token');
      const payload = {
        value: parseInt(formData.value),
        label: formData.label,
        is_active: formData.is_active,
        sort_order: parseInt(formData.sort_order) || 0
      };

      let response;
      if (editingId) {
        response = await fetch(config.getApiUrl(`/products/power-ratings/${editingId}/`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(config.getApiUrl('/products/power-ratings/'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editingId ? 'توان با موفقیت ویرایش شد' : 'توان با موفقیت ایجاد شد');
        setEditingId(null);
        setFormData({ value: '', label: '', is_active: true, sort_order: 0 });
        fetchPowerRatings();
      } else {
        const error = await response.json();
        toast.error(error.error || 'خطا در ذخیره توان');
      }
    } catch (error) {
      console.error('Error saving power rating:', error);
      toast.error('خطا در ارتباط با سرور');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      value: item.value,
      label: item.label,
      is_active: item.is_active,
      sort_order: item.sort_order || 0
    });
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`آیا از حذف توان "${label}" اطمینان دارید؟`)) {
      return;
    }

    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/products/power-ratings/${id}/`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        toast.success('توان با موفقیت حذف شد');
        fetchPowerRatings();
      } else {
        toast.error('خطا در حذف توان');
      }
    } catch (error) {
      console.error('Error deleting power rating:', error);
      toast.error('خطا در ارتباط با سرور');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ value: '', label: '', is_active: true, sort_order: 0 });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="مدیریت توان‌های پکیج | پنل ادمین" noindex={true} />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">مدیریت توان‌های پکیج</h1>
                <p className="text-sm text-gray-500 mt-1">تعریف و مدیریت توان‌های قابل انتخاب برای پکیج‌ها</p>
              </div>
            </div>

            {/* فرم افزودن/ویرایش */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
              <h2 className="text-lg font-semibold mb-4">
                {editingId ? 'ویرایش توان' : 'افزودن توان جدید'}
              </h2>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">مقدار (کیلووات) *</label>
                  <input
                    type="number"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">عنوان نمایشی *</label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="مثال: 10 کیلووات"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ترتیب نمایش</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                    <select
                      value={formData.is_active ? 'true' : 'false'}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="true">فعال</option>
                      <option value="false">غیرفعال</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                  >
                    {editingId ? 'ویرایش' : 'افزودن'}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* لیست توان‌ها */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">مقدار</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">عنوان</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">ترتیب</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">تعداد پکیج</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {powerRatings.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        هیچ توانی تعریف نشده است
                      </td>
                    </tr>
                  ) : (
                    powerRatings.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 font-medium text-gray-900">{item.value}</td>
                        <td className="px-6 py-4">{item.label}</td>
                        <td className="px-6 py-4">{item.sort_order || 0}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {item.is_active ? 'فعال' : 'غیرفعال'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">{item.packages_count || 0}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                              title="ویرایش"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id, item.label)}
                              className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                              title="حذف"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};